import * as Pixi from "pixi.js";

import { V, V2d } from "../Vector";

/**
 * Configuration options for creating a LayerInfo instance.
 *
 * These options control how a rendering layer behaves and appears:
 *
 * @property paralax - How the layer responds to camera movement [x, y]
 *   - [1, 1] = Normal movement (world objects)
 *   - [0, 0] = No movement (UI elements)
 *   - [0.5, 0.5] = Half speed (background parallax effect)
 *
 * @property anchor - Origin point for layer transformations [x, y]
 *   - [0, 0] = Top-left origin (default)
 *   - [0.5, 0.5] = Center origin
 *   - Used for rotation and scaling operations
 *
 * @property filters - Array of Pixi.js filters to apply to the entire layer
 *   - Applied to all sprites in the layer
 *   - More efficient than per-sprite filters
 *
 * @property alpha - Layer transparency (0.0 = invisible, 1.0 = opaque)
 *   - Affects all sprites in the layer
 *   - Useful for fade effects or debugging
 */
export interface LayerInfoOptions {
  paralax?: V2d;
  anchor?: V2d;
  filters?: Pixi.Filter[];
  alpha?: number;
}

/**
 * Configuration and management for a rendering layer.
 *
 * ## Purpose
 *
 * LayerInfo encapsulates all the properties and behavior of a rendering layer,
 * including its Pixi.js container, transformation properties, and visual effects.
 * Each layer acts as an independent rendering group with its own:
 * - Coordinate transformation (parallax, anchoring)
 * - Visual properties (alpha, filters)
 * - Sprite collection (via the container)
 *
 * ## Key Concepts
 *
 * ### Parallax Effects
 * The `paralax` property controls how the layer responds to camera movement:
 * - **[1, 1]**: Layer moves normally with camera (standard world objects)
 * - **[0, 0]**: Layer ignores camera movement (UI elements, HUD)
 * - **[0.5, 0.5]**: Layer moves at half speed (background parallax)
 * - **[2, 2]**: Layer moves faster than camera (foreground effects)
 *
 * ### Anchor Points
 * The `anchor` property defines the origin for transformations:
 * - Affects where rotation and scaling operations occur
 * - Usually [0, 0] for standard layers
 * - Can be used for special effects or UI positioning
 *
 * ### Container Management
 * Each LayerInfo owns a Pixi.js Container that:
 * - Holds all sprites assigned to this layer
 * - Receives transformation updates from the camera
 * - Applies layer-wide visual effects
 *
 * @example
 * ```typescript
 * // Standard world layer
 * const mainLayer = new LayerInfo();
 *
 * // UI layer that doesn't move with camera
 * const hudLayer = new LayerInfo({ paralax: V(0, 0) });
 *
 * // Background with parallax and blur effect
 * const bgLayer = new LayerInfo({
 *   paralax: V(0.3, 0.3),
 *   filters: [new BlurFilter(2)],
 *   alpha: 0.8
 * });
 * ```
 */
export class LayerInfo {
  readonly container: Pixi.Container;
  paralax: V2d;
  anchor: V2d;

  /**
   * Creates a new LayerInfo with the specified configuration.
   *
   * @param options - Layer configuration options
   * @param options.paralax - Camera movement response [x, y] (default: [1, 1])
   * @param options.anchor - Transformation origin [x, y] (default: [0, 0])
   * @param options.filters - Visual filters to apply (default: [])
   * @param options.alpha - Layer transparency (default: 1.0)
   */
  constructor({
    paralax = V(1.0, 1.0),
    anchor = V(0, 0),
    filters = [],
    alpha = 1.0,
  }: LayerInfoOptions = {}) {
    this.container = new Pixi.Container();
    this.paralax = paralax;
    this.anchor = anchor;
    this.container.filters = filters;
    this.container.alpha = alpha;
  }
}
